import React, { useState, useMemo, forwardRef, useEffect, useCallback } from 'react';
import {
  FormControl,
  Select,
  MenuItem,
  ListSubheader,
  TextField,
  InputAdornment,
  InputLabel,
  Checkbox,
  ListItemText,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { getAllEmployeesFilter, getEmployeeById } from 'src/services/employeeService';

interface ResourceDropdownFilterProps {
  title: string;
  value: string;
  onChange: (value: string) => void;
  width?: string;
  disabled?: boolean;
  projectIds?: string[];
  customerIds?: string[];
  startDate?: string;
  endDate?: string;
}

const ResourceDropdownFilter = forwardRef((props: ResourceDropdownFilterProps, _ref) => {
  const [searchText, setSearchText] = useState<string>('');
  const [list, setList] = useState<any[]>([]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const selectedValues = useMemo(() => {
    if (!props.value || props.value.trim() === '') return [];
    return props.value.split(',');
  }, [props.value]);

  const containsText = (text: string, searchText: string) =>
    text?.toLowerCase().indexOf(searchText?.toLowerCase()) > -1;

  const fetchLists = useCallback(async () => {
    const projectIds = props.projectIds || [];
    const customerIds = props.customerIds || [];
    const startDate = props.startDate && props.startDate.trim() !== '' ? props.startDate : null;
    const endDate = props.endDate && props.endDate.trim() !== '' ? props.endDate : null;

    try {
      const res = await getAllEmployeesFilter(customerIds, projectIds, startDate, endDate);
      const newList = Array.isArray(res.data.employees) ? res.data.employees : [];
      
      const validSelections = selectedValues.filter(id => 
        newList.some(employee => employee.employeeId === id)
      );
      
      if (validSelections.length !== selectedValues.length) {
        props.onChange(validSelections.join(','));
      }

      setList(newList);
    } catch (error) {   
      setList([]);

      if (selectedValues.length > 0) {
        props.onChange('');
      }
    }
  }, [props.customerIds, props.projectIds, props.startDate, props.endDate, selectedValues, props.onChange]);

  const fetchSelectedEmployees = useCallback(async (selectedIds: string[]) => {
    const missingIds = selectedIds.filter(id => 
      !list.some(employee => employee.employeeId === id) &&
     
      true 
    );
    
    if (missingIds.length > 0) {
      try {
        const responses = await Promise.all(missingIds.map(id => getEmployeeById(id)));
        const missingEmployees = responses.map(res => res.data);
        const projectIds = props.projectIds || [];
        const customerIds = props.customerIds || [];
        
        const validEmployees = missingEmployees.filter(employee => {
          const matchesCustomer = customerIds.length === 0 || 
            (employee.customerId && customerIds.includes(employee.customerId));
          const matchesProjects = projectIds.length === 0 || 
            (employee.projectIds && projectIds.some(pid => employee.projectIds.includes(pid)));
          return matchesCustomer && matchesProjects;
        });
        
        setList(prevList => [...prevList, ...validEmployees]);
 
        const validIds = validEmployees.map(e => e.employeeId);
        const invalidSelections = missingIds.filter(id => !validIds.includes(id));
        if (invalidSelections.length > 0) {
          const newSelections = selectedValues.filter(id => !invalidSelections.includes(id));
          props.onChange(newSelections.join(','));
        }
      } catch (error) {
        console.error('Error fetching selected employees:', error);
      }
    }
  }, [list, props.projectIds, props.customerIds, selectedValues, props.onChange]);

  const filteredLists = useMemo(() => {
    return list?.filter((option) => containsText(option?.name, searchText));
  }, [searchText, list]);

  const handleSelectChange = (event: any) => {
    const values = event.target.value;
    props.onChange(values.join(','));
  };

  useEffect(() => {
    const loadData = async () => {
      await fetchLists();
      if (isInitialLoad) {
        setIsInitialLoad(false);
      }
    };
    loadData();
  }, [props.projectIds, props.customerIds, fetchLists]);

 
  useEffect(() => {
    if (props.value && props.value.trim() !== '' && !isInitialLoad) {
      fetchSelectedEmployees(props.value.split(','));
    }
  }, [props.value, fetchSelectedEmployees, isInitialLoad]);

  return (
    <FormControl fullWidth disabled={props?.disabled}>
      <InputLabel style={{ fontSize: '12px' }}>
        {props?.title || ''}
      </InputLabel>
      <Select
        id="filter-resource"
        sx={{ mt: 1 }}
        style={{
          width: props?.width || '100%',
          fontSize: '12px',
          height: '2rem',
        }}
        size="small"
        multiple
        value={selectedValues}
        onChange={handleSelectChange}
        onClose={() => setSearchText('')}
        renderValue={(selected) => 
          selected.map((id) => {
            const selectedItem = list.find((item) => item.employeeId === id);
            return selectedItem ? selectedItem.name : id;
          }).join(', ')
        }
      >
        <ListSubheader>
          <TextField
            size="small"
            autoFocus
            placeholder="Type to search..."
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key !== 'Escape') {
                e.stopPropagation();
              }
            }}
          />
        </ListSubheader>
        {filteredLists?.length > 0 ? (
          filteredLists.map((item) => (
            <MenuItem key={item.employeeId} value={item.employeeId}>
              <Checkbox checked={selectedValues.includes(item.employeeId)} />
              <ListItemText primary={item.name} />
            </MenuItem>
          ))
        ) : (
          <Typography
            style={{
              fontSize: '14px',
              margin: '1rem',
              textAlign: 'center',
              color: 'grey',
            }}
            variant="body2"
          >
            {'< - '}No resources found{' - >'}
          </Typography>
        )}
      </Select>
    </FormControl>
  );
});

export default ResourceDropdownFilter;